<template>
  <Modal :open="open" size="medium" @close="$emit('close')">
    <div class="flex flex-col">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-bold text-gray-900">{{ t("title") }}</h2>
        <a
          v-if="intercomUrl"
          :href="intercomUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          {{ t("view_in_intercom") }}
          <ExternalLinkIcon class="h-4 w-4" />
        </a>
      </div>

      <div v-if="loading" class="flex-1 flex items-center justify-center">
        <Spinner />
      </div>

      <p v-else-if="error" class="text-sm text-red-500 py-4 text-center">
        {{ t("error") }}
      </p>

      <template v-else>
        <div class="flex items-center justify-between mb-3">
          <button v-if="hasMoreMessages" type="button" class="text-sm text-primary hover:underline" @click="showMore">
            {{ t("load_more") }}
          </button>
          <span v-else></span>

          <label class="inline-flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
            <input
              v-model="showNotes"
              type="checkbox"
              class="rounded-sm border-gray-300 text-primary focus:ring-primary"
            />
            {{ t("show_notes") }}
          </label>
        </div>

        <div class="flex-1 overflow-y-auto space-y-4 pr-1">
          <IntercomMessageBubble v-for="(message, index) in visibleMessages" :key="index" :message="message" />
        </div>
      </template>
    </div>
  </Modal>
</template>

<script>
import { useI18n } from "vue-i18n";
import { Modal, Spinner, toCamelCase } from "@estaly/ui";
import { ExternalLinkIcon } from "@heroicons/vue/outline";
import IntercomService from "@/api/services/intercom";
import IntercomMessageBubble from "./IntercomMessageBubble.vue";

const MESSAGES_PER_PAGE = 5;

export default {
  name: "IntercomConversationModal",

  components: { Modal, Spinner, ExternalLinkIcon, IntercomMessageBubble },

  emits: ["close"],

  props: {
    open: {
      type: Boolean,
      required: true,
    },
    conversationId: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      loading: false,
      allMessages: [],
      displayCount: MESSAGES_PER_PAGE,
      intercomUrl: null,
      showNotes: false,
      error: false,
    };
  },

  computed: {
    filteredMessages() {
      if (this.showNotes) return this.allMessages;
      return this.allMessages.filter((m) => !m.isNote);
    },

    visibleMessages() {
      const start = Math.max(0, this.filteredMessages.length - this.displayCount);
      return this.filteredMessages.slice(start);
    },

    hasMoreMessages() {
      return this.displayCount < this.filteredMessages.length;
    },
  },

  watch: {
    conversationId(id) {
      if (id) this.fetchConversation(id);
    },
  },

  methods: {
    async fetchConversation(id) {
      this.loading = true;
      this.allMessages = [];
      this.displayCount = MESSAGES_PER_PAGE;
      this.intercomUrl = null;
      this.showNotes = false;
      this.error = false;

      const response = await IntercomService.fetchConversation(id);

      if (this.conversationId !== id) return;

      if (response.success === false) {
        this.error = true;
      } else {
        const conversation = toCamelCase(response.data);
        const source = conversation.source;
        const parts = (conversation.conversationParts || []).filter((part) => part.body !== null);

        this.intercomUrl = conversation.intercomUrl || null;

        this.allMessages = [
          {
            body: source?.body,
            author: source?.author,
            createdAt: conversation.createdAt,
            attachments: source?.attachments || [],
            isNote: false,
          },
          ...parts.map((part) => ({
            body: part.body,
            author: part.author,
            createdAt: part.createdAt,
            attachments: part.attachments || [],
            isNote: !!part.isNote,
          })),
        ];
      }

      this.loading = false;
    },

    showMore() {
      this.displayCount += MESSAGES_PER_PAGE;
    },
  },
};
</script>

<i18n src="./IntercomConversationModal.i18n.json"></i18n>
